import React, { useEffect, useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import PageHero from '../components/PageHero';
import { Shield, FileText, Plus, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePublicSettings } from '../hooks/usePublicSettings';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText } from '../components/InlineEditable';
import { api } from '../lib/api';

const DEFAULT_DOCS = [
  { id: 'kvkk-main', title: 'KVKK Aydınlatma Metni (Tam Metin)', file: '#', description: 'PDF olarak indir' },
];

export default function KVKK() {
  useSEO('kvkk');
  const s = usePublicSettings();
  const [docs, setDocs] = useState(DEFAULT_DOCS);

  useEffect(() => {
    api.publicContent('kvkk_docs').then(list => {
      if (Array.isArray(list) && list.length > 0) {
        setDocs(list.map(it => ({ id: it.id, ...(it.data || {}) })));
      }
    }).catch(() => {});
  }, []);

  return (
    <EditRegion label="KVKK Sayfası" to="/admin/kvkk-docs">
      <>
      <PageHero slug="kvkk"
        kicker="KVKK Yönetmeliği"
        title={<>Kişisel verilerinize <span className="italic text-coral-200">saygımız</span>.</>}
        text="6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında veri işleme politikamız."
      />
      <section className="bg-cream">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-20 md:py-28 space-y-8">
          <div className="bg-white rounded-3xl p-8 md:p-10 border border-ink-100">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8 text-gold" />
              <InlineText settingKey="kvkk_body_title" fallback="Aydınlatma Metni" as="h2"
                className="font-display text-2xl md:text-3xl text-ink-600" />
            </div>
            <InlineText settingKey="kvkk_intro" fallback={'21. Yüzyıl Eğitim ve Kültür Vakfı (“YEKÜV”), 6698 sayılı Kişisel Verilerin Korunması Kanunu kapsamında kişisel verileriniz hakkında sizleri bilgilendirmekten memnuniyet duyar. Vakfımız, “Veri Sorumlusu” sıfatıyla sizden aldığı verileri aşağıda belirtilen amaçlarla işler.'} as="p"
              multiline className="mt-4 text-ink-500 leading-relaxed" />

            <InlineText settingKey="kvkk_purposes_title" fallback="İşleme Amaçları" as="h3"
              className="font-display text-xl mt-6 text-ink-600" />
            <ul className="mt-3 space-y-2 text-sm text-ink-500 list-disc pl-5">
              <li><InlineText settingKey="kvkk_purpose_1" fallback="Burs başvurularınızın değerlendirilmesi" as="span" /></li>
              <li><InlineText settingKey="kvkk_purpose_2" fallback="Bağış ve gönüllülük kayıtlarının tutulması" as="span" /></li>
              <li><InlineText settingKey="kvkk_purpose_3" fallback="Vakıf faaliyetleri hakkında bilgilendirme" as="span" /></li>
              <li><InlineText settingKey="kvkk_purpose_4" fallback="Yasal yükümlülüklerin yerine getirilmesi" as="span" /></li>
            </ul>

            <InlineText settingKey="kvkk_rights_title" fallback="Haklarınız" as="h3"
              className="font-display text-xl mt-6 text-ink-600" />
            <ul className="mt-3 space-y-2 text-sm text-ink-500 list-disc pl-5">
              <li><InlineText settingKey="kvkk_right_1" fallback="Kişisel verilerinizin işlenip işlenmediğini öğrenme" as="span" /></li>
              <li><InlineText settingKey="kvkk_right_2" fallback="İşlenmişse bilgi talep etme" as="span" /></li>
              <li><InlineText settingKey="kvkk_right_3" fallback="İşleme amacı ve uygun kullanılıp kullanılmadığını öğrenme" as="span" /></li>
              <li><InlineText settingKey="kvkk_right_4" fallback="Eksik / yanlış veriyi düzeltme talep etme" as="span" /></li>
              <li><InlineText settingKey="kvkk_right_5" fallback="Silinmesini veya yok edilmesini talep etme" as="span" /></li>
            </ul>
          </div>

          <div className="bg-ink-600 text-cream rounded-3xl p-8 md:p-10">
            <InlineText settingKey="kvkk_contact_title" fallback="İletişim" as="h3"
              className="font-display text-2xl" />
            <InlineText settingKey="kvkk_contact_text" fallback="KVKK kapsamında haklarınızı kullanmak için aşağıdaki kanallarla bize ulaşabilirsiniz:" as="p"
              multiline className="mt-2 text-cream/75" />
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
              <div className="p-4 rounded-xl bg-cream/5 border border-cream/15">
                <InlineText settingKey="kvkk_email_label" fallback="E-posta" as="div"
                  className="text-[10px] uppercase tracking-widest text-gold" />
                <InlineText settingKey="kvkk_email_value" fallback={s.contact_email || 'kvkk@yekuv.org'} as="div"
                  className="mt-1" />
              </div>
              <div className="p-4 rounded-xl bg-cream/5 border border-cream/15">
                <InlineText settingKey="kvkk_postal_label" fallback="Posta" as="div"
                  className="text-[10px] uppercase tracking-widest text-gold" />
                <InlineText settingKey="kvkk_postal_value" fallback={s.contact_address || 'Ortaklar Cad. Bahçeler Sk. No:2/9 Mecidiyeköy'} as="div"
                  multiline className="mt-1" />
              </div>
            </div>
          </div>

          {/* PDF documents list — admin add/manage via /admin/kvkk-docs */}
          <div className="space-y-3" data-testid="kvkk-docs-list">
            {docs.map((d, i) => (
              <EditRegion key={d.id || i} label="Belgeyi Düzenle" to="/admin/kvkk-docs">
                <a
                  href={d.file || '#'}
                  target={d.file && d.file !== '#' ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="block bg-gold/20 border border-gold/40 rounded-3xl p-6 hover:bg-gold/30 transition-colors"
                  data-testid={`kvkk-doc-${d.id || i}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gold text-ink-700 grid place-items-center shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-xl text-ink-600 truncate">{d.title || 'İsimsiz belge'}</div>
                      <div className="text-sm text-ink-500">{d.description || (d.file && d.file !== '#' ? 'PDF olarak aç' : 'Henüz PDF eklenmedi')}</div>
                    </div>
                    {d.file && d.file !== '#' && (
                      <ExternalLink className="w-4 h-4 text-ink-500 shrink-0" />
                    )}
                  </div>
                </a>
              </EditRegion>
            ))}
          </div>

          {/* Admin add-doc CTA — visible only in edit mode */}
          <div className="yekuv-admin-only" style={{ display: 'none' }}>
            <Link
              to="/admin/kvkk-docs"
              data-testid="add-kvkk-doc"
              className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-6 h-12 text-sm font-semibold hover:bg-ink-500"
            >
              <Plus className="w-4 h-4" /> Yeni PDF Belgesi Ekle
            </Link>
          </div>
        </div>
      </section>
    </>
    </EditRegion>
  );
}
